import React, { useState, useMemo } from 'react'
import MDButton from 'components/MDButton'
import MDModal from 'components/MDModal'
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'
import MDInput from 'components/MDInput'
import { useAppServices, useUploadImage } from 'hook/services'
import { Button, Stack, Autocomplete, TextField } from '@mui/material'

const EditModal = ({ data, handleRefresh, paths }) => {
  console.log(paths, 'paths in edit model')
  const AppService = useAppServices()
  const [openEditProduct, setOpenEditProduct] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [selectedPath, setSelectedPath] = useState(null)
  const [searchInput, setSearchInput] = useState('')
  const uploadImage = useUploadImage()

  const handleModal = () => {
    setOpenEditProduct(true)
    // Pre-fill selected path when editing existing category
    if (data?.paths && data.paths.length > 0) {
      const existingPath = data.paths[0]
      const matchedPath = pathOptions.find(option =>
        option.value === existingPath.path || option.label === existingPath.name
      )
      setSelectedPath(matchedPath || null)
    }
  }

  const closeEditProduct = () => {
    setOpenEditProduct(false)
    setSelectedPath(null)
    setSearchInput('')
  }

  // Transform paths for Autocomplete
  const pathOptions = useMemo(() => {
    return paths?.map(path => ({
      label: path.name,
      value: path.path,
      ...path
    })) || []
  }, [paths])

  // Filter options based on search input
  const filteredOptions = useMemo(() => {
    if (!searchInput) return pathOptions
    return pathOptions.filter(option =>
      option.label.toLowerCase().includes(searchInput.toLowerCase()) ||
      option.value.toLowerCase().includes(searchInput.toLowerCase())
    )
  }, [pathOptions, searchInput])

  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent full page reload

    const formData = new FormData(e.target); // Get form values safely

    setProcessing(true);

    let image_response;
    const file = formData.get("image");
    if (file && file.size > 0) {
      image_response = await uploadImage({
        file: file,
        desiredPath: `app/logo/image`,
      });
    }

    // Prepare the paths array for payload
    const pathsPayload = selectedPath ? [
      {
        name: selectedPath.label,
        path: selectedPath.value
      }
    ] : []

    const payload = {
      name: formData.get("name"),
      award_name: formData.get("award_name"),
      description: formData.get("description"),
      type: formData.get("type"),
      status: formData.get("status"),
      points: formData.get("points"),
      paths: pathsPayload, // Add the paths array to payload
      ...(image_response?.response?.data && { image: image_response.response.data }),
    };

    console.log(payload, "payload");

    const { response } = await AppService.checklistcategory.update({
      query: `id=${data._id}`,
      payload,
    });

    if (response) {
      setProcessing(false);
      setOpenEditProduct(false);
      handleRefresh();
    }
  };

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    maxHeight: '80vh',
    overflow: 'auto',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
  }

  return (
    <>
      <MDButton color="info" variant="contained" onClick={handleModal}>
        Edit
      </MDButton>
      <MDModal open={openEditProduct} onClose={closeEditProduct}>
        <MDBox>
          <MDBox
            component="form"
            onSubmit={handleSubmit}
            role="form"
            sx={style}
            encType="multipart/form-data"
          >
            <MDTypography variant="h5" mb={2}>
              Edit Category
            </MDTypography>

            <MDBox mb={2}>
              <MDTypography variant="button">Name</MDTypography>
              <MDInput label="Name" name="name" fullWidth required defaultValue={data?.name} />
            </MDBox>

            <MDBox mb={2}>
              <MDTypography variant="button">Award Name</MDTypography>
              <MDInput
                label="Award Name"
                name="award_name"
                fullWidth
                defaultValue={data.award_name}
              />
            </MDBox>

            {/* Path Selection with Search */}
            <MDBox mb={2}>
              <MDTypography variant="button">Select Path</MDTypography>
              <Autocomplete
                options={filteredOptions}
                value={selectedPath}
                onChange={(event, newValue) => {
                  setSelectedPath(newValue)
                }}
                inputValue={searchInput}
                onInputChange={(event, newInputValue) => {
                  setSearchInput(newInputValue)
                }}
                getOptionLabel={(option) => option.label || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search for a path..."
                    variant="outlined"
                    fullWidth
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <MDBox>
                      <MDTypography variant="body2" fontWeight="bold">
                        {option.label}
                      </MDTypography>
                      <MDTypography variant="caption" color="textSecondary">
                        {option.value}
                      </MDTypography>
                    </MDBox>
                  </li>
                )}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                noOptionsText="No paths found"
              />
            </MDBox>

            <Stack direction="row" alignItems="center" spacing={2}>
              <Button variant="contained" component="label" sx={{ color: '#fff' }}>
                Upload Image +
                <input hidden name="image" type="file" />
              </Button>
            </Stack>

            <MDBox mb={2}>
              <MDTypography variant="button">Description</MDTypography>
              <MDInput
                label="Description"
                name="description"
                fullWidth
                defaultValue={data.description}
              />
            </MDBox>

            <MDBox mb={2}>
              <MDTypography variant="button">Type</MDTypography>
              <MDInput
                select
                name="type"
                fullWidth
                required
                SelectProps={{ native: true }}
                defaultValue={data?.type}
              >
                <option value="agency">Agency</option>
                <option value="location">Location</option>
                {/* <option value="levelupmarketplace">LevelUpMarketplace</option> */}
              </MDInput>
            </MDBox>

            <MDBox mb={2}>
              <MDTypography variant="button">Status</MDTypography>
              <MDInput
                select
                name="status"
                fullWidth
                required
                SelectProps={{ native: true }}
                defaultValue={data.status}
              >
                <option value="Publish">Publish</option>
                <option value="Draft">Draft</option>
              </MDInput>
            </MDBox>

            <MDBox mb={2}>
              <MDTypography variant="button">Point Number</MDTypography>
              <MDInput label="Point Number" name="points" fullWidth defaultValue={data.points} />
            </MDBox>

            <MDBox display="flex" justifyContent="flex-end">
              <MDButton
                variant="gradient"
                color="primary"
                type="button"
                sx={{ mt: 2, mb: 1 }}
                onClick={closeEditProduct}
              >
                Close
              </MDButton>
              <MDButton
                variant="gradient"
                color="info"
                type="submit"
                sx={{ mt: 2, mb: 1, ml: 1 }}
                loading={processing}
                disabled={processing}
              >
                Save
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      </MDModal>
    </>
  )
}

export default EditModal